// Thresholds are generous multiples of the 5-minute Samsara poll: one missed tick must not alarm.
import type { Tone } from "@/lib/tone"

/** Roughly three missed five-minute ticks. */
export const SYNC_STALE_MINUTES = 15

export const SYNC_DOWN_MINUTES = 45

export type Freshness = "fresh" | "stale" | "down" | "unknown"

export function freshnessOf(lastSuccessAt: string | null | undefined, now: Date): Freshness {
  if (!lastSuccessAt) return "unknown"
  const then = new Date(lastSuccessAt)
  if (Number.isNaN(then.getTime())) return "unknown"

  const minutes = (now.getTime() - then.getTime()) / 60_000
  // A future timestamp is clock skew; read it as fresh.
  if (minutes < SYNC_STALE_MINUTES) return "fresh"
  if (minutes < SYNC_DOWN_MINUTES) return "stale"
  return "down"
}

export function freshnessTone(freshness: Freshness): Tone {
  switch (freshness) {
    case "fresh":
      return "neutral"
    case "stale":
      return "warning"
    case "down":
      return "danger"
    case "unknown":
      // No run recorded is normal before the crons land; amber here would train people to ignore amber.
      return "muted"
  }
}

/** Rounded down to the minute so it doesn't read as a countdown. */
export function formatSyncAge(lastSuccessAt: string | null | undefined, now: Date): string {
  if (!lastSuccessAt) return "—"
  const then = new Date(lastSuccessAt)
  if (Number.isNaN(then.getTime())) return "—"

  const minutes = Math.floor((now.getTime() - then.getTime()) / 60_000)
  if (minutes <= 0) return "just now"
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return remainder === 0 ? `${hours}h ago` : `${hours}h ${remainder}m ago`
}
